/**
 * src/lib/hooks/useVoiceModePresentation.js — Voice Mode UI glue (Phase 82-05).
 *
 * Presentation-only hook. Wraps useVoiceConversation (Phase 90) and projects
 * its runtime state onto the overlay's UI contract:
 *   { phase, caption, orbMode, voiceChoice, language, drivingMode,
 *     isMuted, isPaused, controls }
 *
 * Zero network. Zero provider imports. Voice + language are persisted via
 * voiceOptions.js helpers. Orb mode + driving mode are session-ephemeral.
 */

import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import useVoiceConversation from '../../hooks/useVoiceConversation';
import {
  VOICES,
  LANGUAGES,
  resolveVoice,
  resolveLanguage,
  getStoredVoiceId,
  setStoredVoiceId,
  getStoredLanguage,
  setStoredLanguage,
} from '../voice/voiceOptions';
import { logVoiceEvent } from '../voice/voiceLogger';
import { fetchMessages } from '../api/agent';

/**
 * 90-07 AC-1 — Emit a voice_context_bound or voice_context_missing event
 * on voice overlay mount so app_error_logs shows exactly what scope the
 * voice pipeline had when a session started. Exported as a pure helper
 * so the binding contract is unit-testable without mounting the full
 * hook chain.
 *
 * @param {object} [scope]
 * @param {string|null} [scope.project_id]
 * @param {string|null} [scope.deal_id]
 */
export function logContextBinding(scope = {}) {
  const project_id = scope.project_id ?? null;
  const deal_id = scope.deal_id ?? null;
  if (project_id || deal_id) {
    logVoiceEvent(
      'voice_context_bound',
      'ok',
      `Voice overlay bound to scope (project=${project_id || '-'}, deal=${deal_id || '-'})`,
      { project_id, deal_id },
    );
  } else {
    logVoiceEvent(
      'voice_context_missing',
      'error',
      'Voice overlay opened with no scope — agent responses may be unscoped',
      { project_id: null, deal_id: null },
    );
  }
}

const ORB_MODES = ['blob', 'bars', 'dot'];
const CHARS_PER_SEC = 12;

function mapStateToPhase(state, isMuted, isPaused) {
  if (isPaused) return 'paused';
  if (state === 'speaking') return 'speaking';
  if (state === 'processing' || state === 'awaiting_confirmation') return 'thinking';
  // idle | recording | error → show as listening so the orb never flickers
  return 'listening';
}

export default function useVoiceModePresentation({
  conversationId,
  contextProjectId = null,
  contextDealId = null,
  // 2026-04-21 persistence fix — parent (AgentShell) passes setCurrentId
  // so when voice creates a new conversation (conversationId was null on
  // open), the chat view subscribes to it on close instead of seeing the
  // turns "wiped" into an orphan row.
  onConversationPersisted,
  // TICKET-912 slice 3: Quote Copilot voice ({ dealId, quoteId, onTurnDone }) — see useVoiceConversation.
  copilot = null,
} = {}) {
  // Voice Mode overlay IS the continuous-conversation surface — force auto
  // VAD regardless of the user's localStorage preference. Without this,
  // opening the overlay from any non-'/voice' route resolved to 'ptt'
  // (default in voiceMode.readRouteDefault), which silences auto-VAD and
  // leaves the orb idle. (2026-04-20 mobile evidence screenshot.)
  //
  // 2026-04-20 Phase 2 (audit P1-5): pass conversationId so voice turns
  // land in the SAME chat_conversations row as text turns — voice no
  // longer spawns a separate "Voice session" conversation when opened
  // from an existing thread.
  //
  // 90-07 AC-1 (2026-04-21): accept contextProjectId / contextDealId
  // from AgentShell (resolved via deriveCtx(chat.scope)). Previously
  // these were hard-coded null, so the voice pipeline was scope-blind
  // even when the composer knew the context — agent couldn't answer
  // "status of this project" in voice.
  // 2026-04-21 (C1/W4 root-cause fix) — languageId must be declared BEFORE
  // useVoiceConversation so it can thread through to useWhisperRecording →
  // voice.upload_chunk / voice.partial_transcript request bodies. Previously
  // the picker's languageId was cosmetic (declared later, never passed in),
  // and the hook chain hardcoded 'auto' → Whisper auto-detect → Russian
  // boilerplate on silence. See app_error_logs voice_error phrase_count:4x
  // at 2026-04-21 10:17:44 UTC for production evidence.
  const [languageId, setLanguageId] = useState(() => getStoredLanguage());

  const voice = useVoiceConversation({
    contextProjectId,
    contextDealId,
    forceVoiceMode: 'auto',
    existingChatConversationId: conversationId || null,
    onConversationPersisted,
    language: languageId,
    copilot,
  });

  // 90-07 AC-1 — emit one context-binding event per overlay mount so
  // app_error_logs shows exactly what scope the voice pipeline had
  // when a session started. Fires once; `contextProjectId`/`contextDealId`
  // are stable for the overlay's lifetime under the new conditional
  // mount pattern (F1 db93ee5).
  useEffect(() => {
    logContextBinding({ project_id: contextProjectId, deal_id: contextDealId });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only binding event
  }, []);

  const [voiceChoiceId, setVoiceChoiceId] = useState(() => getStoredVoiceId());
  // languageId is declared above (line 103) before useVoiceConversation so
  // the picker value threads through to useWhisperRecording → Whisper STT.
  const [orbMode, setOrbMode] = useState('blob');
  const [drivingMode, setDrivingMode] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  // Phase 082 C2 — transcript channels are typed, never merged. Partial STT
  // flows into `partialTranscript` only; final turns flow into `finalTurns`
  // and never touch `partialTranscript`. System notices (provider errors,
  // audio permission prompts) have their own channel. The overlay renders
  // each channel in its own region — see C3.
  const [finalTurns, setFinalTurns] = useState([]);
  const [systemNotice, setSystemNotice] = useState(null);
  const lastUserTextRef = useRef('');
  const lastAssistantTextRef = useRef('');
  // Tracks the currently-streaming assistant turn id so successive SSE token
  // deltas upsert a single bubble (Claude Code style) instead of appending a
  // new bubble per delta. Cleared when the speaking phase ends.
  const currentAssistantTurnIdRef = useRef(null);

  // Rehydrate finalTurns from chat_messages on mount when a conversationId is
  // provided. Without this, closing + reopening the voice overlay (or
  // reloading the page) wiped the visible turn history even though each turn
  // was persisted server-side via appendMessage(source='voice').
  // 2026-04-20 user report: "when i close the voice pane, info is gone.
  // where is my persistent requirement?" — fixed here.
  const rehydratedForRef = useRef(null);
  useEffect(() => {
    if (!conversationId) return undefined;
    if (rehydratedForRef.current === conversationId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const rows = await fetchMessages(conversationId);
        if (cancelled) return;
        // Include all message rows for this conversation (voice + text) — the
        // voice overlay shows the full timeline so the CEO can continue a
        // chat they started in the composer. source='text' turns render as
        // regular bubbles; source='voice' keeps the "· voice" meta badge.
        const hydrated = (rows || [])
          .filter(r => r && (r.role === 'user' || r.role === 'assistant' || r.role === 'system'))
          .map(r => ({
            id: r.id,
            role: r.role,
            content: r.content || '',
            source: r.source || 'text',
            created_at: r.created_at,
            speaker_label: r.speaker_label || null,
          }));
        if (hydrated.length) {
          setFinalTurns(hydrated);
          // Seed the dedup refs from the tail so the next live SSE delta
          // doesn't duplicate the last turn we just rendered.
          const lastUser = [...hydrated].reverse().find(t => t.role === 'user');
          const lastAssistant = [...hydrated].reverse().find(t => t.role === 'assistant');
          if (lastUser) lastUserTextRef.current = lastUser.content.trim();
          if (lastAssistant) lastAssistantTextRef.current = lastAssistant.content.trim();
        }
        rehydratedForRef.current = conversationId;
      } catch (err) {
        console.warn('[useVoiceModePresentation] rehydrate failed:', err?.message);
      }
    })();
    return () => { cancelled = true; };
  }, [conversationId]);

  // User final: when voice.transcript is non-empty and state transitions away
  // from 'recording', promote to a final turn. Uses the hook's own state
  // (no ref jumble) so React re-renders with the new turn appended.
  useEffect(() => {
    const text = (voice.transcript || '').trim();
    if (!text) return;
    if (text === lastUserTextRef.current) return;
    if (voice.state === 'recording') return; // still rolling, keep as partial
    lastUserTextRef.current = text;
    setFinalTurns((prev) => [...prev, {
      id: `user-${Date.now()}`,
      role: 'user',
      content: text,
      source: 'voice',
      created_at: new Date().toISOString(),
    }]);
  }, [voice.transcript, voice.state]);

  // Assistant streaming: as tokens arrive via SSE, voice.lastAiText /
  // voice.lastAiFullText grow monotonically. We UPSERT into a single open
  // turn so the UI renders one bubble filling in (Claude Code pattern)
  // instead of a new bubble per delta. The open turn closes on the next
  // user turn (see state-transition effect below).
  //
  // 2026-04-20 (session 5) — rAF-coalescing of this upsert was attempted
  // and rolled back. JSDOM-backed unit tests call rerender() synchronously
  // and inspect finalTurns on the next line; async rAF flushes land after
  // that inspection and the tests red. The React.memo added to TurnBubble
  // in this same session already prevents prior bubbles from re-rendering
  // on every token, which was the main TTS-blocking cost. If the remaining
  // reconciliation cost proves measurable at scale we can introduce a
  // microtask-batched flush with a fresh test seam.
  useEffect(() => {
    // 2026-04-20 — CEO report: "voice only gave me this voice queue, but is
    // not what it was written on the transcript." Overlay now renders the
    // SHORT (spoken) text instead of the FULL written text so what the user
    // sees in the voice overlay matches what they hear via TTS. Chat
    // history (chat_messages) still gets the full text — appendMessage
    // uses aiFullTextRef. Overlay = hear-what-you-see; Chat = full record.
    const text = (voice.lastAiText || voice.lastAiFullText || '').trim();
    if (!text) return;
    if (text === lastAssistantTextRef.current) return;
    lastAssistantTextRef.current = text;

    const openId = currentAssistantTurnIdRef.current;
    if (openId) {
      setFinalTurns((prev) => prev.map((t) =>
        t.id === openId ? { ...t, content: text } : t
      ));
    } else {
      const newId = `assistant-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      currentAssistantTurnIdRef.current = newId;
      setFinalTurns((prev) => [...prev, {
        id: newId,
        role: 'assistant',
        content: text,
        source: 'voice',
        created_at: new Date().toISOString(),
      }]);
    }
  }, [voice.lastAiFullText, voice.lastAiText]);

  // Close the open assistant turn when the user starts a new turn. Recording
  // means the user is speaking again, so the next assistant delta must open
  // a NEW bubble, not append to the previous one.
  useEffect(() => {
    if (voice.state === 'recording') {
      currentAssistantTurnIdRef.current = null;
      lastAssistantTextRef.current = '';
    }
  }, [voice.state]);

  // System notice: derived from voice.error. Auto-clears when error resolves.
  useEffect(() => {
    if (voice.error) setSystemNotice(voice.error);
    else setSystemNotice(null);
  }, [voice.error]);

  // Partial transcript: in-flight rolling STT. CEO ask 2026-04-21: "as long
  // as I speak, every few seconds, you get the text feed into the screen. So
  // I have a visual confirmation. Where the translation is going and helps
  // me to pace my voice as well."
  //
  // Previously cleared the moment voice.state left 'recording' — but VAD
  // flips to 'processing' immediately on silence, before finalize returns
  // (~1-3s gap), so the text the user just saw literally disappeared while
  // they waited for the final turn to land. Now kept visible through
  // 'processing' too. It stays until the final turn lands in finalTurns
  // (see lastUserTextRef coordination above) or state returns to 'idle'.
  const partialTranscript = (voice.state === 'recording' || voice.state === 'processing')
    ? (voice.transcript || '')
    : '';

  const [captionActiveIndex, setCaptionActiveIndex] = useState(0);
  const captionRafRef = useRef(null);
  const captionStartRef = useRef(0);
  const lastSpokenTextRef = useRef('');

  const voiceChoice = useMemo(() => {
    try { return resolveVoice(voiceChoiceId); }
    catch { return resolveVoice('nora'); }
  }, [voiceChoiceId]);

  const language = useMemo(() => {
    try { return resolveLanguage(languageId); }
    catch { return resolveLanguage('auto'); }
  }, [languageId]);

  const phase = mapStateToPhase(voice.state, isMuted, isPaused);

  const captionWords = useMemo(() => {
    const text = voice.lastAiText || '';
    if (!text.trim()) return [];
    return text.split(/\s+/).filter(Boolean);
  }, [voice.lastAiText]);

  // Drive the karaoke active-word index from a RAF clock keyed to the text
  // start time. Resets when lastAiText changes. Stops advancing when paused.
  //
  // 2026-04-20 (session 5) — gate on drivingMode. Caption is ONLY rendered
  // by the driving-mode full-screen caption UI; the standard overlay doesn't
  // render caption.words at all. Running a 60fps RAF that calls setState on
  // every frame during non-driving speaking was burning main-thread budget
  // (contributing to TTS gaps) for a value nobody read. Driving mode keeps
  // the karaoke behavior for the few callers who need it.
  useEffect(() => {
    const text = voice.lastAiText || '';
    if (text !== lastSpokenTextRef.current) {
      lastSpokenTextRef.current = text;
      captionStartRef.current = Date.now();
      setCaptionActiveIndex(0);
    }

    if (!drivingMode || !captionWords.length || phase !== 'speaking') {
      if (captionRafRef.current) {
        cancelAnimationFrame(captionRafRef.current);
        captionRafRef.current = null;
      }
      return undefined;
    }

    const tick = () => {
      const elapsedSec = (Date.now() - captionStartRef.current) / 1000;
      const totalChars = text.length || 1;
      const charsElapsed = Math.min(totalChars, elapsedSec * CHARS_PER_SEC);
      // Map charsElapsed to word index by cumulative length.
      let running = 0;
      let idx = 0;
      for (let i = 0; i < captionWords.length; i += 1) {
        running += captionWords[i].length + 1;
        if (running >= charsElapsed) { idx = i; break; }
        idx = i + 1;
      }
      setCaptionActiveIndex(Math.min(idx, captionWords.length - 1));
      captionRafRef.current = requestAnimationFrame(tick);
    };
    captionRafRef.current = requestAnimationFrame(tick);
    return () => {
      if (captionRafRef.current) {
        cancelAnimationFrame(captionRafRef.current);
        captionRafRef.current = null;
      }
    };
  }, [captionWords, phase, voice.lastAiText, drivingMode]);

  const caption = useMemo(() => ({
    words: captionWords,
    activeIndex: captionActiveIndex,
    total: captionWords.length,
  }), [captionWords, captionActiveIndex]);

  // TICKET-073 (karaoke) — map the TTS per-word boundary onto the speaking
  // reply bubble. Consumed by the overlay's TurnList → TurnBubble. (This is the
  // real-playback sync; the chars/sec RAF caption above is the driving-mode
  // fallback and is independent.)
  //
  // The speaking turn is the last assistant turn in finalTurns. We resolve it
  // independently of phase so a live word-boundary signal can never be
  // suppressed by a state-machine timing edge (CEO 2026-06-09: "no karaoke
  // effect at all" — hardening the gate so a momentary phase blip can't blank
  // the highlight). Returned as null when there is no assistant turn yet.
  const lastAssistantTurnId = useMemo(() => {
    for (let i = finalTurns.length - 1; i >= 0; i -= 1) {
      if (finalTurns[i]?.role === 'assistant') return finalTurns[i].id;
    }
    return null;
  }, [finalTurns]);

  const karaoke = useMemo(() => {
    // Barge-in / stop() clears voice.karaoke to null — fully clear the
    // highlight so no stale word lingers between turns.
    if (!voice.karaoke || typeof voice.karaoke.globalWordIndex !== 'number') {
      return { turnId: null, activeWordIndex: -1 };
    }
    // A live word-boundary signal exists. Fire the highlight on the active
    // assistant turn whenever the signal is live (phase-independent) so a
    // 'speaking' state blip can't blank the karaoke. No assistant turn yet →
    // nothing to highlight.
    if (!lastAssistantTurnId) return { turnId: null, activeWordIndex: -1 };
    return { turnId: lastAssistantTurnId, activeWordIndex: voice.karaoke.globalWordIndex };
  }, [lastAssistantTurnId, voice.karaoke]);

  const setVoice = useCallback((id) => {
    try {
      const resolved = setStoredVoiceId(id);
      setVoiceChoiceId(resolved.id);
    } catch (err) {
      console.warn('[useVoiceModePresentation] setVoice failed:', err?.message);
    }
  }, []);

  const setLanguage = useCallback((id) => {
    try {
      const resolved = setStoredLanguage(id);
      setLanguageId(resolved.id);
    } catch (err) {
      console.warn('[useVoiceModePresentation] setLanguage failed:', err?.message);
    }
  }, []);

  const toggleMute = useCallback(() => {
    setIsMuted((prev) => {
      const next = !prev;
      if (next && typeof voice.muteMic === 'function') voice.muteMic();
      else if (!next && typeof voice.unmuteMic === 'function') voice.unmuteMic();
      return next;
    });
  }, [voice]);

  const togglePause = useCallback(() => {
    setIsPaused((prev) => {
      const next = !prev;
      if (next && typeof voice.pauseSpeaking === 'function') voice.pauseSpeaking();
      else if (!next && typeof voice.resumeSpeaking === 'function') voice.resumeSpeaking();
      return next;
    });
  }, [voice]);

  const endSession = useCallback(async () => {
    setIsMuted(false);
    setIsPaused(false);
    // Reset channel state so a fresh voice session starts with a clean overlay.
    setFinalTurns([]);
    setSystemNotice(null);
    lastUserTextRef.current = '';
    lastAssistantTextRef.current = '';
    currentAssistantTurnIdRef.current = null;
    try { await voice.endSession?.(); }
    catch (err) { console.warn('[useVoiceModePresentation] endSession failed:', err?.message); }
  }, [voice]);

  const toggleDriving = useCallback(() => {
    setDrivingMode((prev) => !prev);
  }, []);

  const cycleOrbMode = useCallback(() => {
    setOrbMode((prev) => {
      const idx = ORB_MODES.indexOf(prev);
      return ORB_MODES[(idx + 1) % ORB_MODES.length];
    });
  }, []);

  const controls = useMemo(() => ({
    toggleMute,
    togglePause,
    endSession,
    setVoice,
    setLanguage,
    toggleDriving,
    cycleOrbMode,
    startTurn: voice.startTurn,
    stopRecording: voice.stopRecording,
    // TICKET-097 — press-and-hold orb override (deterministic barge-in).
    holdToTalkStart: voice.holdToTalkStart,
    holdToTalkEnd: voice.holdToTalkEnd,
    // Phase 2 — Retry pill in the error strip calls this to replay the
    // last user transcript when the 2-minute thinking timeout fires.
    retryLastTurn: voice.retryLastTurn,
  }), [toggleMute, togglePause, endSession, setVoice, setLanguage, toggleDriving, cycleOrbMode, voice.startTurn, voice.stopRecording, voice.holdToTalkStart, voice.holdToTalkEnd, voice.retryLastTurn]);

  return {
    phase,
    caption,
    // TICKET-073 (karaoke) — { turnId, activeWordIndex } for the speaking bubble.
    karaoke,
    orbMode,
    voiceChoice,
    language,
    drivingMode,
    isMuted,
    isPaused,
    controls,
    // Phase 082 C2 — typed transcript channels. Never merge these.
    partialTranscript,
    finalTurns,
    systemNotice,
    // Raw state pass-through for the overlay to render error strips,
    // confirmation pill, transcript history, etc.
    raw: {
      state: voice.state,
      transcript: voice.transcript,
      // 2026-04-21 — expose transcribing so the caption can show "Thinking…"
      // during the ~8s Whisper finalize window that sits between user-stop
      // and server-agent-response. Without this the orb text drops to
      // "Speak whenever you're ready" during the wait, which both lies to
      // the user and invites them to start a new utterance on top of a
      // pending one.
      transcribing: voice.transcribing,
      lastAiText: voice.lastAiText,
      error: voice.error,
      pendingProposal: voice.pendingProposal,
      pendingProposals: voice.pendingProposals,
      confirmProposal: voice.confirmProposal,
      cancelProposal: voice.cancelProposal,
      conversationId,
    },
    voices: VOICES,
    languages: LANGUAGES,
  };
}
